import React from 'react';
import {
    View,
    StyleSheet,
    ScrollView,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Share} from 'lucide-react-native';
import {AppDataMediaItem} from "@/data/app-data-interfaces";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import {VideoHeader} from "@/components/media/video-detail/VideoHeader";
import {VideoDescription} from "@/components/media/video-detail/VideoDescription";
import {VideoPlayer} from "@/components/media/video-detail/VideoPlayer";
import {AudioPlayer} from "@/components/media/video-detail/AudioPlayer";
import {HeaderText} from "@/components/common/HeaderText";
import {RelatedVideos} from "@/components/media/video-detail/RelatedVideos";
import {BookmarkButton} from "@/components/media/BookmarkButton";
import {DeleteMediaButton} from "@/components/media/DeleteMediaButton";
import {VideoNavigationControls} from "@/components/media/video-detail/VideoNavigationControls";
import {MediaItemPreview} from "@/components/media/video-detail/MediaItemPreview";

interface VideoDetailProps {
    mediaItem: AppDataMediaItem;
    orgId: string;
    onBack: () => void;
    onShare?: () => void;
    onPreviousVideo?: () => void;
    onNextVideo?: () => void;
    previousVideo?: AppDataMediaItem | null;
    nextVideo?: AppDataMediaItem | null;
    relatedVideos?: AppDataMediaItem[];
    onRelatedSelect?: (videoId: string) => void;
    onDeleted?: () => void;
}

export const MediaDetail: React.FC<VideoDetailProps> = ({
                                                            mediaItem,
                                                            orgId,
                                                            onBack,
                                                            onShare,
                                                            onPreviousVideo,
                                                            onNextVideo,
                                                            previousVideo,
                                                            nextVideo,
                                                            relatedVideos = [],
                                                            onRelatedSelect,
                                                            onDeleted,
                                                        }) => {
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);

    const renderActionButtons = () => (
        <View style={styles.actionButtons}>
            { /* <DownloadButton /> */}

            <BookmarkButton
                orgId={orgId}
                videoId={mediaItem.id}
                variant="default"
                size="medium"
                style={styles.bookmarkButton}
                textStyle={styles.actionButtonText}
                disableMinWidth={true}
            />

            <DeleteMediaButton
                orgId={orgId}
                mediaItem={mediaItem}
                size="medium"
                style={styles.bookmarkButton}
                textStyle={styles.actionButtonText}
                onDeleted={onDeleted}
            />
        </View>
    );

    return (
        <View style={styles.container}>
            <HeaderText
                title={mediaItem.name}
                onBackPress={onBack}
                rightIcon={onShare ? <Share size={20} color={theme.colors.primary} strokeWidth={2}/> : undefined}
                onRightPress={onShare}
            />

            <ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.scrollContent}
            >
                {mediaItem.type === 'video' ? (
                    <VideoPlayer video={mediaItem} orgId={orgId}/>
                ) : mediaItem.type === 'audio' ? (
                    <AudioPlayer audio={mediaItem} orgId={orgId}/>
                ) : (
                    <MediaItemPreview item={mediaItem} orgId={orgId}/>
                )}

                <View style={styles.contentContainer}>
                    <VideoHeader video={mediaItem} orgId={orgId}/>
                    <VideoNavigationControls
                        previousVideo={previousVideo}
                        nextVideo={nextVideo}
                        onPreviousVideo={onPreviousVideo}
                        onNextVideo={onNextVideo}
                    />
                    <VideoDescription video={mediaItem}/>
                    {renderActionButtons()}
                    <RelatedVideos
                        videos={relatedVideos}
                        orgId={orgId}
                        onVideoSelect={onRelatedSelect || (() => {
                        })}
                    />
                </View>
            </ScrollView>
        </View>
    );
};

const createStyles = (theme: ThemeType, insets: {
    top: number;
    bottom: number;
    left: number;
    right: number
}) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    contentContainer: {
        marginHorizontal: Math.max(20, insets.left + 20),
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingBottom: Math.max(theme.spacing.lg, insets.bottom + theme.spacing.lg),
    },
    actionButtons: {
        flexDirection: 'row',
        marginBottom: 20,
        gap: 12,
    },
    actionButton: {
        flex: 1,
        backgroundColor: `${theme.colors.secondary}10`, // Muted secondary color background
        borderWidth: 1,
        borderColor: `${theme.colors.secondary}30`, // Muted secondary color border
        borderRadius: 12,
        padding: 12,
        alignItems: 'center',
        gap: 4,
    },
    actionButtonActive: {
        backgroundColor: `${theme.colors.success}15`,
        borderColor: `${theme.colors.success}40`,
    },
    actionButtonText: {
        color: theme.colors.secondary,
        fontSize: 14,
        fontWeight: '600',
    },
    bookmarkButton: {
        flex: 1,
        // The BookmarkButton will handle its own styling, but we ensure it matches the flex layout
    },
});
